import { useContext, useEffect, useMemo, useRef, useState } from "react";

import { calculateIndemnityWrapper } from "@datanac/datanac-api-toolkit/dist/insurance/indemnity/calculateIndemnityWrapper";
import { round_ex } from "@datanac/datanac-api-toolkit/dist/utility/Math";
import { ApiHelper } from "api/ApiHelper";
import { AppContext } from "App";
import _ from "lodash";

import { InsuranceService } from "./InsuranceService";

const countyCommodityKey = ({ state_name, county_name, commodity_name, crop_year }) =>
  `${state_name}|${county_name}|${commodity_name}|${crop_year}`;

const practiceIsIrrigated = (practice_name) => /irrig/i.test(practice_name || "");

const normalize = (value) => (value || "").toLowerCase();

const lerp = (a, b, t) => a + t * (b - a);

/**
 * Same yield / county-yield / price spans as crop_stress_test.js (defaults when testParameters absent).
 * Exported so the MCO stress grid can share the exact axes of the RP/SCO grids (Rogers).
 */
export const buildStressTestSpans = ({ countyParameters, approved_yield, testParameters = null }) => {
  let yield_low = testParameters?.yield_low ?? 0;
  let yield_high = testParameters?.yield_high;
  if (yield_high == null) {
    yield_high = Math.round(approved_yield * 1.2 || countyParameters?.expected_index_value * 1.2);
  }
  yield_low = Number(yield_low);
  yield_high = Number(yield_high);

  let county_yield_low = testParameters?.county_yield_low ?? 0;
  let county_yield_high = testParameters?.county_yield_high;
  if (county_yield_high == null) {
    county_yield_high = countyParameters?.expected_index_value;
  }

  const yield_step = (yield_high - yield_low) / 9;
  const yield_span = _.range(yield_low, yield_high + yield_step * 0.95, yield_step).map(Math.round);

  const county_yield_step = (county_yield_high - county_yield_low) / 9;
  const county_yield_span = _.range(
    county_yield_low,
    county_yield_high + county_yield_step * 0.95,
    county_yield_step
  ).map(Math.round);

  const projected_price = countyParameters?.projected_price;
  const price_volatility_factor = countyParameters?.price_volatility_factor;
  let price_low = testParameters?.price_low;
  let price_high = testParameters?.price_high;
  if (price_low == null) {
    price_low = round_ex(projected_price - 1 * (price_volatility_factor * projected_price), 4);
  }
  if (price_high == null && price_volatility_factor) {
    price_high = round_ex(projected_price + 1 * (price_volatility_factor * projected_price), 4);
  } else if (price_high == null) {
    price_high = round_ex(projected_price * 1.2, 4);
  }
  price_low = Number(price_low);
  price_high = Number(price_high);

  const price_step = (price_high - price_low) / 9;
  const price_span = _.range(price_low, price_high + price_step * 0.95, price_step).map((v) => round_ex(v, 4));

  return { yield_span, county_yield_span, price_span };
};

const findBracket = (span, value) => {
  if (!span?.length) {
    return { i0: 0, i1: 0, t: 0 };
  }
  if (value <= span[0]) {
    return { i0: 0, i1: 0, t: 0 };
  }
  if (value >= span[span.length - 1]) {
    const last = span.length - 1;
    return { i0: last, i1: last, t: 0 };
  }
  for (let i = 0; i < span.length - 1; i++) {
    if (value >= span[i] && value <= span[i + 1]) {
      const t = span[i + 1] === span[i] ? 0 : (value - span[i]) / (span[i + 1] - span[i]);
      return { i0: i, i1: i + 1, t };
    }
  }
  const last = span.length - 1;
  return { i0: last, i1: last, t: 0 };
};

/**
 * Bilinear interpolation over stress-test grid corners — matches Insurance Benefit at (DATANAC yield, MYA price).
 */
const calculateInterpolatedIndemnity = async ({
  farmParameters,
  countyParameters,
  insurancePlanParameters,
  datanacYield,
  myaPrice,
  approved_yield,
  apiHelper,
}) => {
  const { yield_span, county_yield_span, price_span } = buildStressTestSpans({
    countyParameters,
    approved_yield,
  });

  const farmParametersResolved = {
    ...farmParameters,
    production_cost_per_acre:
      farmParameters.production_cost_per_acre ||
      (farmParameters.reported_acreage > 0 ? farmParameters.production_cost / farmParameters.reported_acreage : 0),
  };

  const yBracket = findBracket(yield_span, datanacYield);
  const pBracket = findBracket(price_span, myaPrice);

  const corners = [
    {
      farmYield: yield_span[yBracket.i0],
      countyYield: county_yield_span[yBracket.i0],
      price: price_span[pBracket.i0],
    },
    {
      farmYield: yield_span[yBracket.i1],
      countyYield: county_yield_span[yBracket.i1],
      price: price_span[pBracket.i0],
    },
    {
      farmYield: yield_span[yBracket.i0],
      countyYield: county_yield_span[yBracket.i0],
      price: price_span[pBracket.i1],
    },
    {
      farmYield: yield_span[yBracket.i1],
      countyYield: county_yield_span[yBracket.i1],
      price: price_span[pBracket.i1],
    },
  ];

  const results = await Promise.all(
    corners.map((corner) =>
      calculateIndemnityWrapper({
        farmParameters: farmParametersResolved,
        countyParameters,
        insurancePlanParameters,
        yield_to_count: corner.farmYield,
        price_to_count: corner.price,
        area_yield_to_count: corner.countyYield,
        apiHelper,
      })
    )
  );

  const perAcre = results.map((r) => r.indemnity_total_per_acre || 0);
  const yt = yBracket.i0 === yBracket.i1 ? 0 : yBracket.t;
  const pt = pBracket.i0 === pBracket.i1 ? 0 : pBracket.t;

  const lowPrice = lerp(perAcre[0], perAcre[1], yt);
  const highPrice = lerp(perAcre[2], perAcre[3], yt);
  const indemnity_total_per_acre = lerp(lowPrice, highPrice, pt);
  const reported_acreage = farmParametersResolved.reported_acreage || 0;

  return {
    indemnity_total_per_acre,
    indemnity_total: indemnity_total_per_acre * reported_acreage,
  };
};

const interpolateAreaYield = ({ yield_to_count, countyParameters, approved_yield }) => {
  const { yield_span, county_yield_span } = buildStressTestSpans({
    countyParameters,
    approved_yield,
  });
  const bracket = findBracket(yield_span, yield_to_count);
  if (bracket.i0 === bracket.i1) {
    return county_yield_span[bracket.i0];
  }
  return Math.round(lerp(county_yield_span[bracket.i0], county_yield_span[bracket.i1], bracket.t));
};

export function useInsuranceProjectedPayment({
  budgetCountyPractices,
  yieldAgg,
  myaPrices,
  crop_year,
  apiConfig = null,
}) {
  const { globalState: contextGlobalState } = useContext(AppContext);
  const globalState = apiConfig || contextGlobalState;

  const [byCounty, setByCounty] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const requestIdRef = useRef(0);

  const bcpsKey = useMemo(
    () =>
      JSON.stringify(
        (budgetCountyPractices || []).map((b) => [
          b.id,
          b.reported_acreage,
          b.yield,
          b.projected_price,
          b.approved_yield,
        ])
      ),
    [budgetCountyPractices]
  );

  const yieldKey = useMemo(() => JSON.stringify(yieldAgg?.byCountyMap || {}), [yieldAgg?.byCountyMap]);
  const myaKey = useMemo(() => JSON.stringify(myaPrices?.pricesByCommodity || {}), [myaPrices?.pricesByCommodity]);
  const producerToken = globalState?.producer_token;

  const isLoading =
    !hasLoadedOnce && (!budgetCountyPractices?.length || yieldAgg?.isLoading || myaPrices?.isLoading || isCalculating);

  useEffect(() => {
    const requestId = ++requestIdRef.current;

    async function calculateAll() {
      if (!crop_year || !producerToken || !budgetCountyPractices?.length) {
        return;
      }
      if (yieldAgg?.isLoading || myaPrices?.isLoading) {
        return;
      }

      setIsCalculating(true);
      try {
        const insuranceService = new InsuranceService({
          crop_year,
          budgetCountyPractices,
          apiConfig: globalState,
          onError: (err) => console.warn("InsuranceService error:", err),
        });

        let scenarios = await insuranceService.actions.loadInsuranceScenario();
        if (scenarios?.length) {
          for (const scenario of scenarios) {
            await insuranceService.actions.calculatePremiums(scenario);
          }
        }

        const populatedBcps = (scenarios || []).flatMap((s) => s.budgetCountyPractices || []);
        const results = [];

        for (const bcp of budgetCountyPractices) {
          const populatedBcp = populatedBcps.find(
            (p) =>
              normalize(p.state_name) === normalize(bcp.state_name) &&
              normalize(p.county_name) === normalize(bcp.county_name) &&
              normalize(p.commodity_name) === normalize(bcp.commodity_name) &&
              normalize(p.type_name) === normalize(bcp.type_name) &&
              normalize(p.practice_name) === normalize(bcp.practice_name)
          );

          const plansForBcp = populatedBcp?.insurancePlans?.filter((p) => p.is_active !== false) || [];
          let countyData = populatedBcp?.countyData || null;

          if (!countyData && populatedBcp) {
            try {
              countyData = await insuranceService.actions.loadCountyData(populatedBcp);
            } catch (err) {
              console.warn("Error loading county data for projected payment:", err);
            }
          }

          const key = countyCommodityKey({
            state_name: bcp.state_name,
            county_name: bcp.county_name,
            commodity_name: bcp.commodity_name,
            crop_year,
          });
          const yieldRow = yieldAgg.byCountyMap?.[key] || {};
          const isIrrigated = practiceIsIrrigated(bcp.practice_name);

          const dnacYield = isIrrigated ? yieldRow.datanacIrrigated : yieldRow.datanacNonIrrigated;
          const rmaFallback = isIrrigated ? yieldRow.rmaProjectedIrrigated : yieldRow.rmaProjectedNonIrrigated;
          const yield_to_count = dnacYield ?? rmaFallback ?? bcp.yield ?? null;

          const myaRow =
            myaPrices.pricesByCommodity?.[bcp.commodity_name] ||
            Object.entries(myaPrices.pricesByCommodity || {}).find(
              ([commodityKey]) => normalize(commodityKey) === normalize(bcp.commodity_name)
            )?.[1];
          const myaPrice = myaRow?.price;
          const price_to_count = myaPrice ?? countyData?.projected_price ?? bcp.projected_price ?? null;

          const cropPlan = plansForBcp.find((p) => p.approved_yield || p.coverage_level_percent);
          const approved_yield = cropPlan?.approved_yield ?? bcp.approved_yield ?? bcp.yield;

          const area_yield_to_count =
            yield_to_count != null && countyData
              ? interpolateAreaYield({ yield_to_count, countyParameters: countyData, approved_yield })
              : yield_to_count;

          let projectedIndemnity = 0;
          let projectedIndemnityPerAcre = 0;

          if (
            plansForBcp.length &&
            yield_to_count != null &&
            price_to_count != null &&
            countyData &&
            bcp.reported_acreage
          ) {
            try {
              const wrapperResult = await calculateInterpolatedIndemnity({
                farmParameters: bcp,
                countyParameters: countyData,
                insurancePlanParameters: plansForBcp,
                datanacYield: yield_to_count,
                myaPrice: price_to_count,
                approved_yield,
                apiHelper: ApiHelper,
              });
              projectedIndemnity = wrapperResult.indemnity_total || 0;
              projectedIndemnityPerAcre = wrapperResult.indemnity_total_per_acre || 0;
            } catch (err) {
              console.warn("Error calculating projected indemnity:", bcp.county_name, bcp.practice_name, err);
            }
          }

          results.push({
            state_name: bcp.state_name,
            county_name: bcp.county_name,
            commodity_name: bcp.commodity_name,
            practice_name: bcp.practice_name,
            acres: bcp.reported_acreage || 0,
            yield_to_count,
            area_yield_to_count,
            price_to_count,
            projectedIndemnity,
            projectedIndemnityPerAcre,
          });
        }

        if (requestId === requestIdRef.current) {
          setByCounty(results);
          setHasLoadedOnce(true);
        }
      } catch (err) {
        console.warn("Error in useInsuranceProjectedPayment:", err);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsCalculating(false);
        }
      }
    }

    calculateAll();
  }, [crop_year, bcpsKey, yieldKey, myaKey, producerToken]);

  const byCommodity = useMemo(() => {
    const grouped = _.groupBy(byCounty, (row) => normalize(row.commodity_name));
    const result = {};

    Object.entries(grouped).forEach(([commodityKey, rows]) => {
      const totalAcres = _.sumBy(rows, "acres");
      const totalIndemnity = _.sumBy(rows, "projectedIndemnity");
      const displayName = rows[0]?.commodity_name || commodityKey;
      result[displayName] = {
        totalIndemnity,
        indemnityPerAcre: totalAcres ? totalIndemnity / totalAcres : 0,
        totalAcres,
        breakdown: rows,
      };
      result[commodityKey] = result[displayName];
    });

    return result;
  }, [byCounty]);

  const farmTotal = useMemo(() => {
    const totalAcres = _.sumBy(byCounty, "acres");
    const totalIndemnity = _.sumBy(byCounty, "projectedIndemnity");
    return {
      totalIndemnity,
      indemnityPerAcre: totalAcres ? totalIndemnity / totalAcres : 0,
      totalAcres,
    };
  }, [byCounty]);

  const lookupByCommodity = (commodityName) => {
    if (!commodityName) return null;
    return byCommodity[commodityName] || byCommodity[normalize(commodityName)] || null;
  };

  return {
    isLoading,
    hasLoadedOnce,
    byCounty,
    byCommodity,
    lookupByCommodity,
    farmTotal,
  };
}

export default useInsuranceProjectedPayment;
